import RequestService, {
    RequestServiceResponse,
} from '@tcc-online/request-service';
import IAdminUser from '../types/users/IAdminUser';
import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import { NotificationActions } from '../../common/actions/NotificationActions';
import Report from '../types/Report';
import AdminUserProject from '../types/users/AdminUserProject';
import UserTypes from '../../common/constants/UserTypes';
import ImportService from './ImportService';
import { isSuccessfulResponse } from '../../helpers';

const BASE_URL = '/users';

const AdminUsersService = {
    getUsers,
    createUser,
    updateUser,
    deleteUser,
    getUser,
    getReports,
    getJobMatchReports,
    getProjects,
    getManagedProjects,
    archive,
    anonymize,
    resetAuth,
    createJobMatch,
    importUsers,
    exportUsers,
    getImportTemplate,
};

const createUrl = (userId?: number) =>
    `${BASE_URL}${!userId ? '' : `/${userId}`}`;

async function getUsers(
    page: number = 1,
): Promise<RequestServiceResponse<IAdminUser[]>> {
    return await RequestService.get<IAdminUser[]>(BASE_URL, { page });
}

interface CreatePayload {
    email: string;
    type: typeof UserTypes.User | typeof UserTypes.Admin;
    firstName?: string;
    lastName?: string;
    language?: string;
}

async function createUser(payload: CreatePayload, thunkApi: GetThunkAPI<any>) {
    const { email, type, firstName, lastName, language } = payload;
    try {
        return await RequestService.post<CreatePayload>(BASE_URL, {
            email,
            type,
            firstName,
            lastName,
            language,
        });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

interface UpdatePayload extends Partial<CreatePayload> {
    userId: number;
}

async function updateUser(payload: UpdatePayload, thunkApi: GetThunkAPI<any>) {
    const { userId, email, language, firstName, lastName, type } = payload;
    try {
        return await RequestService.put(createUrl(userId), {
            email,
            firstName,
            lastName,
            language,
            type,
        });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

async function deleteUser(
    userId: number,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse> {
    try {
        return await RequestService.delete(createUrl(userId));
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se smazat uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

async function getUser(
    payload: {
        userId: number;
    },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<IAdminUser>> {
    try {
        return await RequestService.get<IAdminUser>(createUrl(payload.userId));
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

async function getReports(
    payload: { userId: number; limit?: number },
    thunkApi?: GetThunkAPI<any>,
) {
    const { userId, limit = 5 } = payload;
    try {
        return await RequestService.get<Report[]>(
            createUrl(userId) + '/reports',
            { limit },
        );
    } catch (e) {
        thunkApi?.dispatch(
            NotificationActions.error('nepodarilo se nacist reporty'),
        ); // TODO: translate

        throw e;
    }
}

async function getJobMatchReports(
    payload: { userId: number; limit?: number },
    thunkApi?: GetThunkAPI<any>,
) {
    const { userId, limit = 5 } = payload;
    try {
        return await RequestService.get<Report[]>(
            createUrl(userId) + '/job-matching-reports',
            { limit },
        );
    } catch (e) {
        thunkApi?.dispatch(
            NotificationActions.error('nepodarilo se nacist reporty'),
        ); // TODO: translate

        throw e;
    }
}

async function getProjects(
    payload: { userId: number; limit?: number },
    thunkApi?: GetThunkAPI<any>,
) {
    const { userId, limit = 5 } = payload;
    try {
        return await RequestService.get<AdminUserProject[]>(
            createUrl(userId) + '/projects',
            { limit },
        );
    } catch (e) {
        thunkApi?.dispatch(
            NotificationActions.error('nepodarilo se nacist projekty'),
        ); // TODO: translate

        throw e;
    }
}

async function getManagedProjects(
    payload: { userId: number; limit?: number },
    thunkApi?: GetThunkAPI<any>,
) {
    const { userId, limit = 5 } = payload;
    try {
        return await RequestService.get<AdminUserProject[]>(
            createUrl(userId) + '/managed-projects',
            { limit },
        );
    } catch (e) {
        thunkApi?.dispatch(
            NotificationActions.error('nepodarilo se nacist projekty'),
        ); // TODO: translate

        throw e;
    }
}

async function archive(
    userId: number,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<any, IAdminUser>> {
    try {
        return await RequestService.post<IAdminUser>(
            createUrl(userId) + '/archive',
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se archivovat uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

async function anonymize(
    userId: number,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<any, IAdminUser>> {
    try {
        return await RequestService.post<IAdminUser>(
            createUrl(userId) + '/anonymize',
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se anonymizovat uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

async function resetAuth(
    userId: number,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<any, IAdminUser>> {
    try {
        return await RequestService.post<IAdminUser>(
            createUrl(userId) + '/reset-credentials',
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error(
                'nepodarilo se resetovat udaje uzivatele',
            ),
        ); // TODO: translate

        throw e;
    }
}

async function createJobMatch(
    userId: number,
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<any, IAdminUser>> {
    try {
        return await RequestService.post<IAdminUser>(
            `/admin/job-matchings/from-user/${userId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vyvorit job match'),
        ); // TODO: translate

        throw e;
    }
}

async function importUsers(
    payload: { companyId: number; data: FormData },
    thunkApi: GetThunkAPI<any>,
) {
    const { companyId, data } = payload;
    try {
        const result = await RequestService.post<FormData>(
            `/admin/companies/${companyId}/users/import`,
            data,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        if (isSuccessfulResponse(result.status)) {
            ImportService.saveImportId(result.data.importId);
        }

        return result;
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se importovat uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

async function getImportTemplate(
    companyId: number,
    thunkApi: GetThunkAPI<any>,
) {
    try {
        return await RequestService.get<Blob>(
            `/admin/companies/${companyId}/users/import/template`,
            {},
            { responseType: 'blob' },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se ziskat sablonu'),
        ); // TODO: translate

        throw e;
    }
}

async function exportUsers(companyId: number, thunkApi: GetThunkAPI<any>) {
    try {
        return await RequestService.get<Blob>(
            `/admin/companies/${companyId}/users/export`,
            {},
            { responseType: 'blob' },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se exportovat uzivatele'),
        ); // TODO: translate

        throw e;
    }
}

export default AdminUsersService;
